// Copyright 1999-2017. Plesk International GmbH. All rights reserved.

import { ModalDialogBox } from './modal-dialog-box';
import { prepareUrl, showInternalError } from './common';

const factory = config => {
    if (config.isWebspace) {
        return new WebspaceFoldersMessageBox(config);
    }

    if (config.isAjax) {
        return new MultiCheckboxMessageWindow(config);
    }

    return new MessageBox(config);
};

/**
 * @class Jsw.messageBox
 * @singleton
 */
export const messageBox = {
    /**
     * @property {String} TYPE_OK
     */
    TYPE_OK: 'TYPE_OK',

    /**
     * @property {String} TYPE_YESNO
     */
    TYPE_YESNO: 'TYPE_YESNO',

    _messageBox: null,

    /**
     * Show message box.
     * @param {Object} config
     */
    show(config) {
        messageBox._messageBox
            ? messageBox._messageBox.initialize(config)
            : messageBox._messageBox = factory(config);

        messageBox._messageBox.show();
    },
};

/**
 * @class Jsw.MessageBox
 * @extends Jsw.ModalDialogBox
 */
export const MessageBox = Class.create(ModalDialogBox, {
    _initConfiguration($super, config) {
        $super(config);

        this._cls = 'popup-panel popup-panel-sm popup-panel-centered confirmation-box';
        this._type = this._getConfigParam('type', messageBox.TYPE_YESNO);
        this._text = this._getConfigParam('text', '');
        this._subtype = this._getConfigParam('subtype', null);
        this._description = this._getConfigParam('description', '');
        this._descriptionWrapperTag = this._getConfigParam('descriptionWrapperTag', 'p');
        this._buttonTitles = this._getConfigParam('buttonTitles', null);

        this._onYesClick = this._getConfigParam('onYesClick', null);
        this._onNoClick = this._getConfigParam('onNoClick', null);
        this._onOkClick = this._getConfigParam('onOkClick', null);
        this._needAttention = this._getConfigParam('needAttention', false);
        this._needAttentionText = this._getConfigParam('needAttentionText', '');
        this._needAttentionBlockSubmit = this._getConfigParam('needAttentionBlockSubmit', false);

        if (messageBox.TYPE_YESNO === this._type) {
            if (this._onYesClick) {
                this._onYesClickHandler = this._onYesClick.bindAsEventListener(this);
            } else {
                this._onYesClickHandler = this._defaultOnButtonClick.bindAsEventListener(this);
            }
        }
    },

    _getDescriptionCode() {
        const wrapDescription = description => `<${this._descriptionWrapperTag}>${description}</${this._descriptionWrapperTag}>`;

        if (Object.isArray(this._description)) {
            return this._description.map(wrapDescription).join('');
        }

        return wrapDescription(this._description);
    },

    _getNeedAttentionCode() {
        if (!this._needAttention) {
            return '';
        }

        let needAttentionCode = '<hr>';
        if (this._needAttentionBlockSubmit) {
            needAttentionCode += (
                '<p>' +
                    '<span class="b-indent">' +
                        '<span class="b-indent-icon">' +
                            '<input type="checkbox" class="checkbox" id="needAttentionConfirmationCheckbox">' +
                        '</span>' +
                        `<label for="needAttentionConfirmationCheckbox">${this._needAttentionText}</label>` +
                    '</span>' +
                '</p>'
            );
        } else {
            needAttentionCode += `<p class="hint-warning">${this._needAttentionText}</p>`;
        }
        needAttentionCode += '<hr>';

        return needAttentionCode;
    },

    _initComponentElement($super) {
        $super();

        const descriptionCode = this._getDescriptionCode();
        const needAttentionCode = this._getNeedAttentionCode();

        let contentHtml =
            '<div class="popup-wrapper">' +
                '<div class="popup-container">' +
                    '<div class="popup-content">' +
                        '<div class="popup-content-area">' +

                            `<div class="confirmation-msg${this._subtype ? ` mw-${this._subtype}` : ''}">` +
                                `<h3>${this._text}</h3>` +
                                `${descriptionCode}${needAttentionCode}` +
                                '<div class="btns-container">';

        if (messageBox.TYPE_YESNO === this._type) {
            const buttonDefault = this._getConfigParam('buttonDefault', 'no');
            contentHtml += `<button class="btn${'yes' === buttonDefault ? ' action' : ''}" type="button">${this._buttonTitles.yes}</button>`;
            contentHtml += `<button class="btn${'no' === buttonDefault ? ' action' : ''}" type="button">${this._buttonTitles.no}</button>`;
        }

        if (messageBox.TYPE_OK === this._type) {
            contentHtml += `<button class="btn action" type="button">${this._buttonTitles.ok}</button>`;
        }

        contentHtml += '</div>' +
                            '</div>' +

                        '</div>' +
                    '</div>' +
                '</div>' +
            '</div>';

        this._updateComponentElement(contentHtml);

        this._componentElement.hide();
    },

    _disableYesButton() {
        if (messageBox.TYPE_YESNO === this._type) {
            const buttonYes = this._componentElement.down('.btns-container .btn');
            buttonYes.addClassName('disabled').disable();
            Event.stopObserving(buttonYes, 'click', this._onYesClickHandler);
        }
    },

    _enableYesButton(button) {
        if (messageBox.TYPE_YESNO === this._type) {
            const buttonYes = this._componentElement.down('.btns-container .btn');
            buttonYes.removeClassName('disabled').enable();
            Event.observe(buttonYes, 'click', this._onYesClickHandler);
        }
    },

    _addEvents($super) {
        $super();

        // add default behavior - hide dialog
        this._componentElement.select('button').each(function (button) {
            Event.observe(button, 'click', this._defaultOnButtonClick.bindAsEventListener(this));
        }, this);

        if (messageBox.TYPE_YESNO === this._type) {
            if (this._onYesClick) {
                const buttonYes = this._componentElement.select('button').first();
                Event.observe(buttonYes, 'click', this._onYesClickHandler);
            }

            if (this._onNoClick) {
                const buttonNo = this._componentElement.select('button').last();
                Event.observe(buttonNo, 'click', this._onNoClick.bindAsEventListener(this));
            }

            if (this._needAttention && this._needAttentionBlockSubmit) {
                const confirmationCheckbox = this._componentElement.select('input').last();
                const top = this;
                Event.observe(confirmationCheckbox, 'click', function () {
                    if ($F(confirmationCheckbox)) {
                        top._enableYesButton();
                    } else {
                        top._disableYesButton();
                    }
                });
            }
        }

        if (messageBox.TYPE_OK === this._type) {
            if (this._onOkClick) {
                const buttonOk = this._componentElement.select('button').first();
                Event.observe(buttonOk, 'click', this._onOkClick.bindAsEventListener(this));
            }
        }
    },

    _addTooltips() {
    },

    _defaultOnButtonClick() {
        this.hide();
    },

    show($super) {
        $super();
        this._componentElement.select('.action').first().focus();
        if (this._needAttention && this._needAttentionBlockSubmit) {
            this._disableYesButton();
        }
    },
});

/**
 * @class Jsw.AjaxMessageBox
 * @extends Jsw.MessageBox
 */
export const AjaxMessageBox = Class.create(MessageBox, {
    _initConfiguration($super, config) {
        $super(config);
        this._requestUrl = this._getConfigParam('requestUrl', '');
        this._requestMethod = this._getConfigParam('requestMethod', 'post');
        this._requestParams = this._getConfigParam('requestParams', '');
        this._contentContainerId = this._getConfigParam('contentContainerId', `${this._id}contentContainer`);
        this._confirmationCheckboxId = this._getConfigParam('confirmationCheckboxId', `${this._id}confirmationCheckbox`);
        this._loadingTitle = this._getConfigParam('loadingTitle', '');
        if (this._progress) {
            return;
        }
        this._progress = false;
    },

    initialize($super, config) {
        if (this._progress) {
            return;
        }
        $super(config);
    },

    _getDescriptionCode() {
        return '';
    },

    _getNeedAttentionCode($super) {
        return `<div id="${this._contentContainerId}"></div>${$super()}`;
    },

    _showSpinner() {
        if (messageBox.TYPE_YESNO === this._type) {
            this._progress = true;
            this._componentElement.select('button').first().update(`<span class="wait">${this._buttonTitles.wait}</span>`);
        }
    },

    _hideSpinner(button) {
        if (messageBox.TYPE_YESNO === this._type) {
            this._progress = false;
            this._componentElement.select('button').first().update(this._buttonTitles.yes);
        }
    },

    show($super) {
        $super();
        if (!this._requestUrl || this._progress) {
            return;
        }

        new Ajax.Request(prepareUrl(this._requestUrl), {
            method: this._requestMethod,
            parameters: this._requestParams,
            onCreate: this._onCreate.bind(this),
            onSuccess: this._onSuccess.bind(this),
            onFailure: this._onFailure.bind(this),
        });
    },

    _onCreate() {
        this._disableYesButton();
        this._showSpinner();
    },

    _onSuccess() {
        this._hideSpinner();
        this._enableYesButton();
    },

    _onFailure() {
        this._hideSpinner();
        this._enableYesButton();
    },
});

/**
 * @class Jsw.MultiCheckboxMessageWindow
 * @extends Jsw.AjaxMessageBox
 */
export const MultiCheckboxMessageWindow = Class.create(AjaxMessageBox, {
    _onSuccess(transport) {
        this._hideSpinner();

        const result = transport.responseText.evalJSON();

        const { content } = result;
        let contentCode = '';
        if (Object.isArray(content)) {
            if (content.length > 0) {
                contentCode = '<ul class="ul">';
                content.each(function (item) {
                    contentCode += `<li>${item}</li>`;
                });
                contentCode += '</ul>';
            }
        } else {
            contentCode = content;
        }

        let descriptionCode = '';
        if (result.description) {
            descriptionCode = `<p><span class="hint-warning">${result.description}</span></p>`;
        }

        let confirmationCode = '';
        if (result.confirmation) {
            confirmationCode = '<p><span class="b-indent">' +
                    '<span class="b-indent-icon">' +
                        `<input type="checkbox" class="checkbox" id="${this._confirmationCheckboxId}"/>` +
                    '</span>' +
                    `<label for="${this._confirmationCheckboxId}">${result.confirmation}</label>` +
                '</span></p>';
        }

        $(this._contentContainerId).update(contentCode + descriptionCode + confirmationCode);

        const checkboxes = this._componentElement.select('input[type="checkbox"]');
        const top = this;
        checkboxes.each(function (checkbox) {
            Event.stopObserving(checkbox, 'click');
            Event.observe(checkbox, 'click',
                function () {
                    let enableYesButton = true;
                    top._componentElement.select('input[type="checkbox"]').each(function (confirmationCheckbox) {
                        if (!$F(confirmationCheckbox)) {
                            enableYesButton = false;
                        }
                    });
                    if (enableYesButton) {
                        top._enableYesButton();
                    } else {
                        top._disableYesButton();
                    }
                }
            );
        });

        if (!checkboxes.length) {
            this._enableYesButton();
        }
    },
});

/**
 * @class Jsw.WebspaceFoldersMessageBox
 * @extends Jsw.AjaxMessageBox
 */
export const WebspaceFoldersMessageBox = Class.create(AjaxMessageBox, {
    _initConfiguration($super, config) {
        $super(config);
        this._successDescription = this._getConfigParam('successDescription', '');
        this._failureDescription = this._getConfigParam('failureDescription', '');
    },

    initialize($super, config) {
        $super(config);
        this._setDescription([]);
    },

    _setDescription(folders) {
        const list = folders.map(function (item) {
            return `<li>${item.name.escapeHTML()}</li>`;
        });
        const message = folders.length
            ? this._description.concat(' ').concat(this._successDescription).concat(`<ul>${list.join('')}</ul>`)
            : this._description;
        $(this._contentContainerId).update(message);
    },

    _onSuccess(transport) {
        this._hideSpinner();
        this._enableYesButton();
        try {
            this._setDescription(transport.responseText.evalJSON().data);
        } catch (e) {
            showInternalError(transport.responseText);
        }
    },

    _onFailure($super) {
        $super();
        $(this._contentContainerId).update(`${this._description} ${this._failureDescription}`);
    },
});



// WEBPACK FOOTER //
// ./jsw/message-box.js